import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const BasicFinancialRatios = () => {
  const title = "Temel Finansal Oranlar: Akıllı Yatırım İçin Temel Araçlar ";
  const description = "Temel finansal oranların ne olduğunu ve yatırım kararlarınızı nasıl etkilediğini öğrenin. Bu makalede, finansal oranların nasıl hesaplandığını ve nasıl kullanılabileceğini keşfedin.";
 
  const canonicalUrl = "https://smart-portfoy.com/egitim/basic-financial-ratios"; // Replace with your actual URL

  const sections = [
    {
      title: 'Finansal Oranlar Nedir?',
      description: 'Finansal oranlar, bir şirketin mali durumunu ve performansını değerlendirmek için kullanılan matematiksel hesaplamalardır.',
      usageInfo: {
        importance: 'Finansal oranlar, yatırımcıların ve analistlerin bir şirketin mali sağlığını ve performansını hızlı bir şekilde değerlendirmelerine yardımcı olur.',
        usage: 'Finansal oranları kullanarak, şirketlerin mali durumunu ve performansını karşılaştırabilir ve yatırım kararlarınızı daha bilinçli bir şekilde alabilirsiniz.'
      }
    },
    {
      title: 'Likidite Oranları',
      description: 'Likidite oranları, bir şirketin kısa vadeli borçlarını ödeme yeteneğini ölçer:',
      points: [
        'Cari Oran',
        'Hızlı Oran',
        'Nakit Oranı'
      ]
    },
    {
      title: 'Kârlılık Oranları',
      description: 'Kârlılık oranları, bir şirketin kârlılığını ve verimliliğini ölçer:',
      points: [
        'Brüt Kâr Marjı',
        'Faaliyet Kâr Marjı',
        'Net Kâr Marjı',
        'Özsermaye Kârlılığı (ROE)',
        'Aktif Kârlılığı (ROA)'
      ]
    },
    {
      title: 'Borçluluk Oranları',
      description: 'Borçluluk oranları, bir şirketin borç yükünü ve finansal kaldıraç seviyesini ölçer:',
      points: [
        'Borç/Özsermaye Oranı',
        'Borç/Toplam Varlık Oranı',
        'Faiz Karşılama Oranı'
      ]
    },
    {
      title: 'Piyasa Değeri Oranları',
      description: 'Piyasa değeri oranları, bir şirketin piyasa değerini ve hisse senedi performansını ölçer:',
      points: [
        'Fiyat/Kazanç Oranı (P/E)',
        'Piyasa Değeri/Defter Değeri Oranı (P/B)',
        'Fiyat/Satış Oranı (P/S)',
        'Temettü Verimi'
      ]
    },
    {
      title: 'Smart Portfoy ile Finansal Oranları Takip Edin',
      description: 'Smart Portfoy ile finansal oranları ve diğer yatırım araçlarını kolayca takip edin, otomatik raporlar oluşturun ve yatırım kararlarınızı daha bilinçli bir şekilde alın.',
      points: []
    }
  ];

  return (
    <>
 
 <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta name="keywords" content="finansal oranlar, yatırım, finansal analiz, kârlılık, likidite, borçluluk, piyasa değeri" />
      </Helmet>




      <div className="min-h-screen bg-gray-800 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-indigo-900 py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-4 mt-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
              {title}
            </h1>
            <p className="text-lg text-gray-300 max-w-3xl leading-relaxed">
              {description}
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/blog/basic-financial-ratios/basic-financial-ratios-guide.png" 
            alt="Temel Finansal Oranlar İllüstrasyonu"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-8">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <section key={index} className="space-y-6 p-6 bg-gray-900/50 rounded-lg hover:bg-blue-900/50 transition duration-300 ease-in-out">
                <h2 className="text-2xl font-bold text-blue-400">{section.title}</h2>
                <p className="text-gray-300">{section.description}</p>
                
                {section.usageInfo && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2">Önemi</h3>
                      <p className="text-gray-300">{section.usageInfo.importance}</p>
                    </div>
                    
                    <div className="bg-gray-800/50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2">Nasıl Kullanılır</h3>
                      <p className="text-gray-300">{section.usageInfo.usage}</p>
                    </div>
                  </div>
                )}

                {section.points && section.points.length > 0 && (
                  <ul className="space-y-2">
                    {section.points.map((point, pIndex) => (
                      <li key={pIndex} className="flex items-start space-x-2">
                        <span className="flex-shrink-0 w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                          {pIndex + 1}
                        </span>
                        <span className="text-gray-300">{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default BasicFinancialRatios;